import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import DateRangeFilter, { getDateRange } from '../../../components/DateRangeFilter';

const InvoiceCenterTab = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [preset, setPreset] = useState('last_30');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const extractState = (address) => {
    if (!address) return '';
    const parts = address.split(',').map(p => p.trim());
    return parts[parts.length - 1]?.toLowerCase() || '';
  };

  const loadInvoices = useCallback(async (from, to) => {
    setLoading(true);
    try {
      let query = supabase
        ?.from('buyer_bookings')
        ?.select('id, readable_booking_id, factory_name, grade, quantity, rate_per_ton, total_amount, status, created_at, booking_date, dispatched_at, buyer_email, loading_from, destination, assigned_vehicle_number, booking_type')
        ?.in('status', ['in_transit', 'completed', 'delivered'])
        ?.order('created_at', { ascending: false });

      if (from) query = query.gte('booking_date', from.toISOString().slice(0, 10));
      if (to) query = query.lte('booking_date', to.toISOString().slice(0, 10));

      const { data, error } = await query;
      if (error) throw error;

      const derived = (data || [])?.map((b, idx) => {
        const subtotal = Number(b?.total_amount) || (Number(b?.quantity) * Number(b?.rate_per_ton)) || 0;
        const sourceState = extractState(b?.loading_from);
        const destState = extractState(b?.destination);
        const isInterState = sourceState && destState && sourceState !== destState;
        const igst = isInterState ? subtotal * 0.05 : 0;
        const cgst = isInterState ? 0 : subtotal * 0.025;
        const sgst = isInterState ? 0 : subtotal * 0.025;
        const totalAmount = subtotal + igst + cgst + sgst;
        const bookingDate = b?.booking_date || b?.created_at || new Date()?.toISOString();

        return {
          id: `INV${String(idx + 1)?.padStart(7, '0')}`,
          bookingId: b?.readable_booking_id || b?.id,
          invoiceNumber: `SH/INV/${new Date(bookingDate)?.getFullYear()}/${String(idx + 1)?.padStart(3, '0')}`,
          date: bookingDate?.slice(0, 10),
          mill: b?.factory_name || '—',
          buyerEmail: b?.buyer_email || '—',
          product: b?.grade || '—',
          quantity: Number(b?.quantity) || 0,
          rate: Number(b?.rate_per_ton) || 0,
          vehicleNumber: b?.assigned_vehicle_number || '—',
          loadingFrom: b?.loading_from || '—',
          destination: b?.destination || '—',
          subtotal,
          igst,
          cgst,
          sgst,
          isInterState,
          totalAmount,
          status: (() => {
            if (b?.status === 'completed' || b?.status === 'delivered') return 'Delivered';
            if (b?.status === 'in_transit' && b?.dispatched_at) {
              const days = (Date.now() - new Date(b.dispatched_at).getTime()) / (1000 * 60 * 60 * 24);
              return days >= 4 ? 'Delivered' : 'Pending';
            }
            return 'Pending';
          })(),
        };
      });

      setInvoices(derived);
    } catch (err) {
      console.error('QM InvoiceCenterTab error:', err?.message);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const { from: dateFrom, to: dateTo } = getDateRange(preset, customFrom, customTo);

  useEffect(() => {
    loadInvoices(dateFrom, dateTo);
  }, [loadInvoices, preset, customFrom, customTo]);

  const filteredInvoices = invoices?.filter(inv => {
    const q = searchText?.toLowerCase() || '';
    return !q || [
      inv?.invoiceNumber, inv?.bookingId, inv?.mill,
      inv?.buyerEmail, inv?.product, inv?.vehicleNumber, inv?.destination
    ].some(f => (f || '')?.toLowerCase()?.includes(q));
  });

  const getStatusColor = (status) => {
    return status === 'Delivered' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold" style={{ color: '#1f2937' }}>Invoice Center</h2>
          <p className="text-sm mt-0.5" style={{ color: '#4b5563' }}>
            {loading ? 'Loading…' : `${filteredInvoices?.length} invoice${filteredInvoices?.length !== 1 ? 's' : ''} found`}
          </p>
        </div>
        <button
          onClick={() => loadInvoices(dateFrom, dateTo)}
          className="self-start sm:self-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
        >
          <Icon name="RefreshCw" size={13} />
          Refresh
        </button>
      </div>

      {/* Search & Date Filter */}
      <div className="flex flex-col gap-2">
        <input
          type="text"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          placeholder="Search by Invoice No, Booking ID, factory, buyer, grade, vehicle, destination..."
          className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
        />
        <DateRangeFilter
          preset={preset} customFrom={customFrom} customTo={customTo}
          onPresetChange={setPreset} onCustomFromChange={setCustomFrom} onCustomToChange={setCustomTo}
        />
      </div>

      {/* Loading */}
      {loading && (
        <div className="text-center py-12 rounded-xl" style={{ background: '#fdf8f0', border: '1px solid #e8ddd0' }}>
          <div className="w-8 h-8 border-4 border-amber-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm" style={{ color: '#9ca3af' }}>Loading invoices...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && invoices?.length === 0 && (
        <div className="text-center py-16 rounded-xl" style={{ background: '#fdf8f0', border: '1px solid #e8ddd0' }}>
          <Icon name="FileText" size={40} color="#d1a87a" />
          <p className="mt-3 text-sm font-medium" style={{ color: '#9ca3af' }}>No invoices found</p>
          <p className="text-xs mt-1" style={{ color: '#d1a87a' }}>
            Try a different date range to find older records
          </p>
        </div>
      )}

      {/* No search results */}
      {!loading && invoices?.length > 0 && filteredInvoices?.length === 0 && (
        <div className="text-center py-12 rounded-xl" style={{ background: '#fdf8f0', border: '1px solid #e8ddd0' }}>
          <Icon name="Search" size={32} color="#d1a87a" />
          <p className="mt-3 text-sm font-medium" style={{ color: '#9ca3af' }}>No invoices match your search</p>
        </div>
      )}

      {/* Desktop Table */}
      {!loading && filteredInvoices?.length > 0 && (
        <>
          <div className="hidden lg:block overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  {['Invoice No', 'Booking ID', 'Date', 'Factory', 'Buyer', 'Grade', 'Qty (MT)', 'Rate/Ton', 'Vehicle', 'GST Type', 'Subtotal', 'Tax', 'Total', 'Status'].map(col => (
                    <th key={col} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 whitespace-nowrap">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredInvoices?.map((inv, idx) => (
                  <tr
                    key={inv?.id}
                    style={{ background: idx % 2 === 0 ? '#ffffff' : '#fafafa', borderBottom: '1px solid #f3f4f6' }}
                  >
                    <td className="px-4 py-3 text-xs font-mono font-semibold text-gray-700 whitespace-nowrap">{inv?.invoiceNumber}</td>
                    <td className="px-4 py-3 text-xs font-mono text-gray-600 whitespace-nowrap">{inv?.bookingId}</td>
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{inv?.date}</td>
                    <td className="px-4 py-3 text-xs text-gray-700 max-w-[150px]">{inv?.mill}</td>
                    <td className="px-4 py-3 text-xs text-gray-600 max-w-[160px]">{inv?.buyerEmail}</td>
                    <td className="px-4 py-3">
                      <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-700">
                        {inv?.product}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs font-medium text-gray-800">{inv?.quantity}</td>
                    <td className="px-4 py-3 text-xs text-gray-700 whitespace-nowrap">₹{Number(inv?.rate)?.toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-xs font-mono text-gray-700 whitespace-nowrap">{inv?.vehicleNumber}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${inv?.isInterState ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>
                        {inv?.isInterState ? 'IGST 5%' : 'CGST+SGST'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-700 whitespace-nowrap">₹{inv?.subtotal?.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                    <td className="px-4 py-3 text-xs text-gray-700 whitespace-nowrap">
                      ₹{(inv?.isInterState ? inv?.igst : inv?.cgst + inv?.sgst)?.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </td>
                    <td className="px-4 py-3 text-xs font-semibold text-green-700 whitespace-nowrap">₹{inv?.totalAmount?.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getStatusColor(inv?.status)}`}>
                        {inv?.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden space-y-4">
            {filteredInvoices?.map(inv => (
              <div key={inv?.id} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-xs font-mono font-semibold text-gray-700">{inv?.invoiceNumber}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{inv?.bookingId}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getStatusColor(inv?.status)}`}>
                    {inv?.status}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <p className="text-gray-400">Factory</p>
                    <p className="font-medium text-gray-700">{inv?.mill}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Buyer</p>
                    <p className="font-medium text-gray-600 truncate">{inv?.buyerEmail}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Grade</p>
                    <p className="font-medium text-gray-700">{inv?.product}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Quantity</p>
                    <p className="font-medium text-gray-700">{inv?.quantity} MT</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Vehicle</p>
                    <p className="font-mono font-medium text-gray-700">{inv?.vehicleNumber}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Date</p>
                    <p className="font-medium text-gray-700">{inv?.date}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">GST Type</p>
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${inv?.isInterState ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>
                      {inv?.isInterState ? 'IGST 5%' : 'CGST+SGST'}
                    </span>
                  </div>
                  <div>
                    <p className="text-gray-400">Tax</p>
                    <p className="font-medium text-gray-700">₹{(inv?.isInterState ? inv?.igst : inv?.cgst + inv?.sgst)?.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
                  </div>
                </div>
                <div className="border-t border-gray-100 pt-2 flex items-center justify-between">
                  <p className="text-xs text-gray-400">Total Amount</p>
                  <p className="text-base font-bold text-green-700">₹{inv?.totalAmount?.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default InvoiceCenterTab;

